import { Box, Button } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import { RiFileDownloadLine } from "react-icons/ri";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import CustomTabs from '../../Components/Common/CustomTabs';

import { Buyers, AllUsers, Sellers, InactiveUsers } from '../../Components/Users'
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_USER_LIST, GET_BUYERS_LIST, GET_SELLERS_LIST, GET_INACTIVE_USERS_LIST } from '../../Utils/Queries';
import Loader from '../../Components/Common/Loader';
import CustomSearch from '../../Components/Common/CustomSearch';

const TAB_QUERIES = [
  GET_USER_LIST,
  GET_BUYERS_LIST,
  GET_SELLERS_LIST,
  GET_INACTIVE_USERS_LIST,
];

const TAB_LABELS = ['All Users', 'Buyers', 'Sellers', 'Inactive Users'];

/** Same matching rules as handleSearch — applied to full exported list when search text is set. */
function filterUsersForSearch(users, searchTermRaw) {
  const searchTerm = searchTermRaw.trim().toLowerCase();
  if (!searchTerm) return users ?? [];
  return (users ?? []).filter(
    (item) =>
      `${item?.firstName || ''} ${item?.lastName || ''}`.toLowerCase().includes(searchTerm) ||
      item?._id?.toLowerCase().includes(searchTerm) ||
      item?.email?.toLowerCase().includes(searchTerm) ||
      item?.phone?.toLowerCase().includes(searchTerm),
  );
}

const Users = () => {
  const [value, setValue] = React.useState(0);
  const [searchParams, setSearchParams] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const activeQuery = useMemo(() => TAB_QUERIES[value] || GET_USER_LIST, [value]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setPage(1);
    setSearchParams("");
  };

  const { data, loading } = useQuery(activeQuery, {
    variables: {
      page,
      limit: rowsPerPage,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [searchedItems, setSearchedItems] = useState([])

  const client = useApolloClient();
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    setSearchedItems(data?.listAllUsers?.items || [])
  }, [data])

  const handleSearch = (e) => {
    const raw = e.target.value;
    const filteredItems = filterUsersForSearch(data?.listAllUsers?.items, raw);
    setSearchedItems(filteredItems);
    setSearchParams(raw);
  };

  const handleChangePageLeft = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleChangePageRight = () => {
    const totalPages = data?.listAllUsers?.pagination?.totalPages || 1;
    setPage((prevPage) => Math.min(prevPage + 1, totalPages));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const sharedTabProps = {
    data: searchedItems,
    page,
    rowsPerPage,
    pagination: data?.listAllUsers?.pagination,
    onPreviousPage: handleChangePageLeft,
    onNextPage: handleChangePageRight,
    onRowsPerPageChange: handleChangeRowsPerPage,
    isSearching: Boolean(searchParams),
  };

  const TabData = [
    {
      label: "All Users",
      component: <AllUsers {...sharedTabProps} />,
    },
    {
      label: "Buyers",
      component: <Buyers {...sharedTabProps} />,
    },
    {
      label: "Sellers",
      component: <Sellers {...sharedTabProps} />,
    },
    {
      label: "Inactive Users",
      component: <InactiveUsers {...sharedTabProps} />,
    },
  ];

  const generatePDF = async () => {
    const totalItems = Number(data?.listAllUsers?.pagination?.totalItems ?? 0);
    if (!totalItems || pdfLoading) {
      return;
    }

    const query = TAB_QUERIES[value];
    setPdfLoading(true);

    try {
      const { data: exportData } = await client.query({
        query,
        variables: {
          page: 1,
          limit: Math.max(totalItems, 1),
        },
        fetchPolicy: 'network-only',
      });

      let exportRows = exportData?.listAllUsers?.items ?? [];
      exportRows = filterUsersForSearch(exportRows, searchParams);

      const doc = new jsPDF();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      const tabTitle = TAB_LABELS[value] ?? 'Users';
      doc.text(
        `Mamaket Users (${tabTitle}) — ${exportRows.length} row(s) — ${formatDate(new Date())}`,
        10,
        10,
      );
      doc.setFont('helvetica');
      doc.setFontSize(12);

      const tableColumn = [
        'S/N',
        'ID',
        'Name',
        'Email',
        'Phone',
        'Location',
        'Profile',
        'Status',
        'Date',
      ];
      const tableRows = [];

      exportRows.forEach((item, index) => {
        const itemData = [
          index + 1,
          item._id,
          `${item.firstName} ${item.lastName}`,
          item.email,
          item.phone,
          `${item?.addresses[0]?.city?.name || '--'}, ${item?.addresses[0]?.city?.state?.name || '--'}, ${item?.addresses[0]?.city?.state?.country?.name || '--'}`,
          item.hasPaidMembershipFee ? 'Seller' : 'Buyer',
          item.activityStatus,
          formatDate(item?.createdAt),
        ];
        tableRows.push(itemData);
      });

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 15,
        styles: {
          fontSize: 6,
          font: 'helvetica',
        },
        headStyles: {
          fillColor: [92, 48, 105],
        },
      });

      doc.save(
        `mamaket_ul_${tabTitle.replace(/\s+/g, '_')}_${Date.now()}.pdf`,
      );
    } catch (err) {
      console.error('[Users] PDF export failed', err);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <Box sx={{ bgcolor: "#fff", p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ width: "70%" }}>
            <CustomSearch placeholder={"Search by ID, product, or others..."} value={searchParams} onChange={handleSearch} />
          </Box>
          <Box
            sx={{
              width: "15%",
              bgcolor: "#F8F7F7",
              fontSize: "12px",
              display: "flex",
              columnGap: 1,
              alignItems: "center",
              borderRadius: 1,
              justifyContent: "center",
            }}
          >
          </Box>
          <Button
            onClick={generatePDF}
            disabled={loading || pdfLoading || !data?.listAllUsers?.pagination?.totalItems}
            endIcon={<RiFileDownloadLine style={{ color: "#666" }} />}
            sx={{
              color: "#666",
              bgcolor: "#F8F7F7",
              width: "10%",
              py: 1,
              fontSize: "12px",
            }}
          >
            {pdfLoading ? 'Preparing…' : 'Download'}
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <CustomTabs
            value={value}
            handleChange={handleChange}
            data={TabData}
            variant="standard"
          />
        </Box>
      </Box>
    </>
  );
}

export default Users

const formatDate = (dateString) => {
  const date = new Date(dateString);

  const day = date.getUTCDate();
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
};
